import { Injectable, OnDestroy } from '@angular/core';
import { CredentialsTokensType } from '@cargos/sprintpay-models';
import { TokenService } from '@cargos/sprintpay-services';
import { BehaviorSubject, Observable, filter, from, fromEvent, map, of, take } from 'rxjs';
import { SessionWorkerMessage, SessionWorkerMessageTypes } from './session.worker';

@Injectable()
export class SessionWorkerService implements OnDestroy {
    private sharedWorker: SharedWorker;
    private isInitialized: boolean = false;
    private port: MessagePort;
    private tokens: CredentialsTokensType | null = null;
    private blockPort: MessagePort | null = null;
    public updateTokens$: BehaviorSubject<CredentialsTokensType | null> =
        new BehaviorSubject<CredentialsTokensType | null>(null);

    constructor(private tokenService: TokenService) {
        if (window.SharedWorker && !this.isUserOnSafariMobile()) {
            this.sharedWorker = new SharedWorker(new URL('./session.worker', import.meta.url));
            this.sharedWorker.port.onmessage = this.handleMessage;
            this.port = this.sharedWorker.port;
            this.subscribeInitializeSession();
            this.subscribePageHide();
        }
    }

    ngOnDestroy(): void {
        this.updateTokens$.complete();
    }

    private handleMessage = (event: MessageEvent<SessionWorkerMessage>): void => {
        const { data } = event;
        event.preventDefault();
        switch (event.data.type) {
            case SessionWorkerMessageTypes.REQUEST_TOKENS:
                if (!this.tokens && !this.blockPort) {
                    this.blockPort = this.port;
                    this.sharedWorker.port.postMessage({
                        tokens: this.tokens,
                        isRequestAllowed: true,
                    });
                } else if (
                    (!this.tokens && this.blockPort) ||
                    (this.tokens && !this.blockPort && !data.isInitialized)
                ) {
                    this.sharedWorker.port.postMessage({
                        tokens: this.tokens,
                        isRequestAllowed: false,
                    });
                } else if (this.tokens && !this.blockPort && data.isInitialized) {
                    this.blockPort = this.port;
                    this.sharedWorker.port.postMessage({
                        tokens: null,
                        isRequestAllowed: true,
                    });
                } else if (this.tokens && this.blockPort && this.blockPort === this.port) {
                    this.sharedWorker.port.postMessage({
                        tokens: null,
                        isRequestAllowed: true,
                    });
                }
                break;
            case SessionWorkerMessageTypes.IS_TOKEN_REFRESH_ALLOWED:
                if (!this.blockPort) {
                    this.blockPort = this.port;
                    this.sharedWorker.port.postMessage({ isRequestAllowed: true });
                } else {
                    this.sharedWorker.port.postMessage({ isRequestAllowed: false });
                }
                break;
            case SessionWorkerMessageTypes.UPDATE_TOKENS:
                const tokens = event.data.tokens;
                this.updateTokens$.next(tokens || null);
                break;
            case SessionWorkerMessageTypes.CLOSE_CONNECTION:
                if (this.blockPort === this.port) {
                    this.blockPort = null;
                }
                break;
        }
    };

    private subscribeInitializeSession(): void {
        this.tokenService
            .getRefreshToken$()
            .pipe(
                filter((tokens) => !!tokens),
                map(() => {
                    this.isInitialized = true;
                }),
                take(1)
            )
            .subscribe();
    }

    private subscribePageHide(): void {
        fromEvent(window, 'pagehide')
            .pipe(
                map(() => {
                    this.postMessage({
                        type: SessionWorkerMessageTypes.CLOSE_CONNECTION,
                    });
                }),
                take(1)
            )
            .subscribe();
    }

    private postMessage(message: SessionWorkerMessage): void {
        this.sharedWorker.port.postMessage(message);
    }

    private postPromisifiedMessage(message: SessionWorkerMessage): Promise<SessionWorkerMessage> {
        return new Promise((resolve) => {
            const channel = new MessageChannel();
            channel.port1.onmessage = ({ data }) => {
                channel.port1.close();
                resolve(data);
            };

            this.sharedWorker.port.postMessage(message, [channel.port2]);
        });
    }

    public requestTokens(): Observable<SessionWorkerMessage> {
        if (!this.sharedWorker) {
            return of({
                type: SessionWorkerMessageTypes.REQUEST_TOKENS,
                tokens: null,
                isRequestAllowed: true,
            });
        }

        return from(
            this.postPromisifiedMessage({
                type: SessionWorkerMessageTypes.REQUEST_TOKENS,
                isInitialized: this.isInitialized,
            })
        );
    }

    public isTokenRefreshAllowed(): Observable<SessionWorkerMessage> {
        if (!this.sharedWorker) {
            return of({
                type: SessionWorkerMessageTypes.IS_TOKEN_REFRESH_ALLOWED,
                tokens: null,
                isRequestAllowed: true,
            });
        }

        return from(
            this.postPromisifiedMessage({
                type: SessionWorkerMessageTypes.IS_TOKEN_REFRESH_ALLOWED,
            })
        );
    }

    public postTokens(tokens: CredentialsTokensType | null): void {
        if (this.sharedWorker) {
            this.postMessage({
                type: SessionWorkerMessageTypes.UPDATE_TOKENS,
                tokens,
            });
        }
    }

    public isUserOnSafariMobile(): boolean {
        const isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document);

        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        return isIOS && isSafari;
    }
}
